import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { setThemePreference } from '@/api/theme/set-theme-preference';
import { SettingsContext } from '@/providers/SettingsProvider';
import { resolveTheme } from '@/providers/ThemeProvider/resolveTheme';
import type { Theme } from '@/types/theme';
export interface IThemeContext {
  /**
   * The concrete theme the page is painted in, with `'system'` already
   * resolved. Use this — not `theme` — whenever you need a literal colour.
   */
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  theme: Theme;
  toggleTheme: () => void;
}
interface ThemeProviderProps {
  children: React.ReactNode;
}
const DEFAULT_THEME: Theme = 'system';
export const ThemeContext = React.createContext<IThemeContext>({
  setTheme: () => null,
  toggleTheme: () => null,
  theme: DEFAULT_THEME,
  resolvedTheme: 'light'
});
ThemeContext.displayName = 'ThemeContext';
const getInitialTheme = (themePreference?: number): Theme => {
  if (themePreference === -1) {
    return DEFAULT_THEME;
  }
  if (themePreference === 2) {
    return 'system';
  }
  return themePreference === 0 ? 'light' : 'dark';
};
const getSystemTheme = (): 'light' | 'dark' => window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
const applyThemeClass = (theme: 'light' | 'dark') => {
  if (theme === 'dark') {
    document.documentElement.classList.remove('theme-light');
    document.documentElement.classList.add('theme-dark');
  } else {
    document.documentElement.classList.remove('theme-dark');
    document.documentElement.classList.add('theme-light');
  }
};
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children
}) => {
  const {
    settings
  } = useContext(SettingsContext);
  const themePreference = settings?.theme_preference;
  const userId = settings?.user?.id ? Number(settings.user.id) : undefined;
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => getInitialTheme(themePreference));

  // Tracked regardless of `currentTheme` so `resolvedTheme` stays correct even
  // for users whose backend preference short-circuits `loadInitialTheme`.
  // Starts false so SSR and first paint agree; the effect corrects it on mount.
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  // Ref to track the transition re-enable timeout
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const applyThemeWithTransition = useCallback((theme: 'light' | 'dark') => {
    // On mount loadInitialTheme re-applies the theme the inline _document
    // script already set, so without this every page load toggled
    // disable-transitions for 50ms for no visual reason.
    if (document.documentElement.classList.contains(`theme-${theme}`)) {
      return;
    }

    // Clear any existing timeout to prevent race conditions
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    // Disable transitions during theme change
    document.documentElement.classList.add('disable-transitions');
    applyThemeClass(theme);

    // Re-enable transitions after the theme change has been applied
    transitionTimeoutRef.current = setTimeout(() => {
      document.documentElement.classList.remove('disable-transitions');
      transitionTimeoutRef.current = null;
    }, 50);
  }, []);
  const setTheme = useCallback((newTheme: Theme) => {
    if (newTheme === undefined || currentTheme === newTheme) {
      return;
    }
    setCurrentTheme(newTheme);
    if (newTheme === 'system') {
      applyThemeWithTransition(getSystemTheme());
    } else {
      applyThemeWithTransition(newTheme);
    }
    setThemePreference(newTheme, userId).catch((error: unknown) => {
      console.warn('Could not save theme preference', error);
    });
    localStorage.setItem('theme', newTheme);
  }, [currentTheme, applyThemeWithTransition, userId]);
  const loadInitialTheme = useCallback(() => {
    const theme = localStorage.getItem('theme') as Theme;
    if (theme === 'system') {
      setCurrentTheme('system');
      applyThemeWithTransition(getSystemTheme());
      return;
    }
    if (theme === 'dark') {
      setCurrentTheme('dark');
      applyThemeWithTransition('dark');
      return;
    }
    if (theme === 'light') {
      setCurrentTheme('light');
      applyThemeWithTransition('light');
      return;
    }

    // No theme set - default to system
    setCurrentTheme('system');
    applyThemeWithTransition(getSystemTheme());
  }, [applyThemeWithTransition]);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => setSystemPrefersDark(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener('change', sync);
    return () => {
      mediaQuery.removeEventListener('change', sync);
    };
  }, []);

  // Listen to system preference changes when theme is 'system'
  useEffect(() => {
    if (currentTheme !== 'system') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      applyThemeWithTransition(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [currentTheme, applyThemeWithTransition]);

  // Toggle between light and dark themes
  // If currently on 'system', switch to the opposite of what system is showing
  const toggleTheme = useCallback(() => {
    const currentVisualTheme = currentTheme === 'system' ? getSystemTheme() : currentTheme;
    const newTheme = currentVisualTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  }, [currentTheme, setTheme]);
  const ctx = useMemo((): IThemeContext => ({
    setTheme,
    toggleTheme,
    theme: currentTheme,
    resolvedTheme: resolveTheme(currentTheme, systemPrefersDark)
  }), [setTheme, toggleTheme, currentTheme, systemPrefersDark]);
  useEffect(() => {
    // A theme was set according to the user's preferences.
    if (themePreference && themePreference !== -1) {
      return;
    }
    loadInitialTheme();
  }, [themePreference, loadInitialTheme]);

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
  }, []);
  return <ThemeContext.Provider value={ctx} data-sentry-element="ThemeContext.Provider" data-sentry-component="ThemeProvider" data-sentry-source-file="index.tsx">{children}</ThemeContext.Provider>;
};